import React, { useRef, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import "@/styles/lookups.css";

/** The shared look-up box: type, pick one of the matches, and the form fills itself. Waiting and errors sit
    inline under the box (never over the fields), so typing the details in by hand is never blocked. */
export function LookupBox({ label, hint, note, placeholder, value, onChange, items, busy, error, onPick, render, testId, className = "h-11", dark = false, waitText }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const show = open && items.length > 0 && !error;
  const waiting = busy && !items.length && !error && waitText;
  const ready = !open && items.length > 0 && !busy && !error;
  const slot = waitText && (value || "").trim().length > 0;  // keeps the line's space so nothing jumps under the cursor
  const n = items.length;
  return (
    <div className={`lk${dark ? " lk-dark" : ""}`} data-testid={testId}>
      <label className="lk-label">
        <Search className="size-3.5" aria-hidden="true" /> {label}
        {hint && <span className="lk-hint">{hint}</span>}
      </label>
      <div className="lk-field">
        <Input ref={ref} value={value} placeholder={placeholder} className={className} autoComplete="off"
               onChange={(e) => { onChange(e.target.value); setOpen(true); }}
               onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
               data-testid={`${testId}-input`} />
        {busy && <Loader2 className="lk-spin size-4 animate-spin" aria-hidden="true" />}
        {show && (
          <ul className="lk-menu" role="listbox" data-testid={`${testId}-results`}>
            {items.map((it, i) => (
              <li key={i}>
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setOpen(false); onPick(it); }} data-testid={`${testId}-option-${i}`}>
                  {render(it)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {(slot || error || note) && (
        <div className="lk-line" role="status">
          {error ? <p className="lk-err" data-testid={`${testId}-error`}>{error}</p>
            : waiting ? <p className="lk-wait" data-testid={`${testId}-searching`}>{waitText}</p>
            : ready ? <button type="button" className="lk-ready" onClick={() => ref.current?.focus()} data-testid={`${testId}-ready`}>
                        {n} match{n === 1 ? "" : "es"} found — tap to pick one</button>
            : note ? <span className="lk-note" data-testid={`${testId}-note`}><Check className="size-3" strokeWidth={3} aria-hidden="true" />{note}</span>
            : null}
        </div>
      )}
    </div>
  );
}
